import { Controller, type FieldValues, type Path } from 'react-hook-form';
import Autocomplete, {
    type AutocompleteRenderInputParams
} from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
    personName,
    personNames,
    personPickerProps,
    type PersonPickerConfig
} from '../personPicker';
import {
    appendAutocompleteIndicator,
    mergeAutocompleteAdornment
} from '../mergeAutocompleteAdornment';
import { indicatorRoomSx } from '../fieldEndButton';
import {
    recentKeyDownProps,
    showsRecommendedHint,
    type FormFieldBinding
} from './binding';
import { recommendedWarning } from './recommendedWarning';
import { joinedToChips, chipsToJoined } from './joinedChips';
import { chipClipboardProps } from '../chipClipboard';
import TabSuggestAutocomplete from '../TabSuggestAutocomplete';

// ---------------------------------------------------------------------------
// The person fields — Ausgräber, Fotograf, Zeichner, Autor, Grabungsleiter and
// the rest. A PICKER over the app's accounts, never free text: see
// ../personPicker for what that buys. The `config` comes from
// `usePersonPicker()`, held once by the form.
// ---------------------------------------------------------------------------

/** The label + error decoration every person field's input carries. */
function personInput<Values extends FieldValues>(
    binding: FormFieldBinding<Values>,
    name: Path<Values>,
    value: unknown,
    fieldError: { message?: string } | undefined,
    inputRef: React.Ref<HTMLInputElement>,
    /** The F2 button, bare — null where the field offers none. */
    recentTrigger: React.ReactNode,
    /** The one name set is no account's; several people mark their chips. */
    unlinked = false
) {
    return function renderInput(params: AutocompleteRenderInputParams) {
        return (
            <TextField
                {...params}
                inputRef={inputRef}
                label={binding.intl.formatMessage({
                    id: binding.labelId(name)
                })}
                error={!!fieldError}
                helperText={
                    fieldError
                        ? binding.validationMessage(fieldError.message)
                        : undefined
                }
                slotProps={{
                    ...params.slotProps,
                    input: {
                        ...params.slotProps.input,
                        ...(unlinked && {
                            startAdornment: (
                                <InputAdornment
                                    position="start"
                                    title={binding.intl.formatMessage({
                                        id: 'person.unlinked'
                                    })}
                                >
                                    <WarningAmberIcon
                                        color="warning"
                                        fontSize="small"
                                    />
                                </InputAdornment>
                            )
                        }),
                        endAdornment: mergeAutocompleteAdornment(
                            appendAutocompleteIndicator(
                                params.slotProps.input.endAdornment,
                                recentTrigger
                            ),
                            recommendedWarning(
                                binding.intl,
                                showsRecommendedHint(binding, name, value),
                                true
                            )
                        )
                    }
                }}
            />
        );
    };
}

/** One person: a picker over the accounts, plus whatever the record says. */
export function renderPersonField<Values extends FieldValues>(
    binding: FormFieldBinding<Values>,
    name: Path<Values>,
    config: PersonPickerConfig,
    { leads = false }: { leads?: boolean } = {}
) {
    return (
        <Controller
            name={name}
            control={binding.control}
            render={({ field, fieldState }) => {
                const value = `${field.value ?? ''}`;
                const recentTrigger = binding.recentTrigger?.(name);
                // MUI shows its clear X while a name is set (unless read
                // only) — the F2 button needs room beside it.
                const clearable = !binding.readOnly && value !== '';
                return (
                    <TabSuggestAutocomplete
                        {...personPickerProps(config, {
                            current: value,
                            leads
                        })}
                        onTake={(picked) => field.onChange(personName(picked))}
                        sx={
                            recentTrigger
                                ? indicatorRoomSx(clearable ? 2 : 1)
                                : undefined
                        }
                        readOnly={binding.readOnly}
                        value={value || null}
                        onChange={(_event, picked) =>
                            field.onChange(personName(picked))
                        }
                        onBlur={field.onBlur}
                        {...recentKeyDownProps(binding, name)}
                        fullWidth
                        renderInput={personInput(
                            binding,
                            name,
                            field.value,
                            fieldState.error,
                            field.ref,
                            recentTrigger,
                            value !== '' && !config.isLinked(value)
                        )}
                    />
                );
            }}
        />
    );
}

interface PersonMultiOptions {
    leads?: boolean;
    /** Why a chip is to be looked at, or null; flagged chips are drawn red. */
    flag?: (name: string) => string | null;
}

/** Several people, over a real `string[]` field (e.g. a sheet's Zeichner). */
export function renderPersonMultiField<Values extends FieldValues>(
    binding: FormFieldBinding<Values>,
    name: Path<Values>,
    config: PersonPickerConfig,
    options: PersonMultiOptions = {}
) {
    return personMulti(
        binding,
        name,
        config,
        {
            toChips: (value) =>
                Array.isArray(value) ? (value as string[]) : [],
            fromChips: (chips) => chips
        },
        options
    );
}

/**
 * Several people over a field that stores them ', '-joined (the Tagebuch's
 * Techniker and Personal, which is the wire format ExcaBook reads).
 */
export function renderPersonMultiJoinedField<Values extends FieldValues>(
    binding: FormFieldBinding<Values>,
    name: Path<Values>,
    config: PersonPickerConfig,
    options: PersonMultiOptions = {}
) {
    return personMulti(
        binding,
        name,
        config,
        {
            toChips: (value) => joinedToChips(value, config.knownNames),
            fromChips: chipsToJoined
        },
        options
    );
}

/** The chips list both codecs draw; only the value adapter differs. */
function personMulti<Values extends FieldValues>(
    binding: FormFieldBinding<Values>,
    name: Path<Values>,
    config: PersonPickerConfig,
    codec: {
        toChips: (value: unknown) => string[];
        fromChips: (chips: string[]) => unknown;
    },
    { leads = false, flag }: PersonMultiOptions = {}
) {
    return (
        <Controller
            name={name}
            control={binding.control}
            render={({ field, fieldState }) => {
                const chips = codec.toChips(field.value);
                const picker = personPickerProps(config, {
                    current: chips,
                    multiple: true,
                    leads
                });
                return (
                    <Autocomplete
                        {...picker}
                        readOnly={binding.readOnly}
                        // A pasted name no account carries is the free text
                        // only `nameFreely` may write (see ../personPicker).
                        {...chipClipboardProps({
                            value: chips,
                            getLabel: (name: string) => name,
                            options: picker.options,
                            fromText: config.freeText
                                ? (text) => text
                                : undefined,
                            onChange: (next) =>
                                field.onChange(
                                    codec.fromChips(config.distinct(next))
                                ),
                            readOnly: binding.readOnly
                        })}
                        multiple
                        // Picking a crew is several picks in a row — closing the
                        // list after each one would reopen it each time.
                        disableCloseOnSelect
                        value={chips}
                        onChange={(_event, picked) =>
                            field.onChange(
                                codec.fromChips(
                                    config.distinct(
                                        personNames(picked).filter(Boolean)
                                    )
                                )
                            )
                        }
                        onBlur={field.onBlur}
                        fullWidth
                        // A name no account carries stays free text on save —
                        // it counts as somebody else in every later comparison.
                        renderValue={(names, getItemProps) =>
                            names.map((person, index) => {
                                const { key, ...chipProps } = getItemProps({
                                    index
                                });
                                const label = personName(person);
                                const flagged = flag?.(label) ?? null;
                                if (flagged) {
                                    return (
                                        <Chip
                                            key={key}
                                            size="small"
                                            color="error"
                                            label={label}
                                            title={flagged}
                                            {...chipProps}
                                        />
                                    );
                                }
                                return config.isLinked(label) ? (
                                    <Chip
                                        key={key}
                                        size="small"
                                        label={label}
                                        {...chipProps}
                                    />
                                ) : (
                                    <Chip
                                        key={key}
                                        size="small"
                                        variant="outlined"
                                        color="warning"
                                        icon={<WarningAmberIcon />}
                                        label={label}
                                        title={binding.intl.formatMessage({
                                            id: 'person.unlinked'
                                        })}
                                        {...chipProps}
                                    />
                                );
                            })
                        }
                        renderInput={personInput(
                            binding,
                            name,
                            field.value,
                            fieldState.error,
                            field.ref,
                            null
                        )}
                    />
                );
            }}
        />
    );
}
